import { Select, SelectValue, SelectOptionValue } from '@tencent/udesign/dist';
import {
  getAllowClearConfig,
  getDynamicSelectedValue,
  getDynamicSelectOption,
  getPlaceholderConfig,
  getSelectTypeConfig,
  getTableBodyLinkageValue,
  isSelectComponent,
  getCheckItem,
  AUTO_TABLE_CUSTOM_VALUE_ALL,
  getCheckValue,
  getTraverseValue,
  addPos,
  getParentIndex,
} from '../../../libs';
import {
  FieldType,
  IFieldValueExtensionSelect,
  IFieldTypeOptBySelect,
  IPropsSelect,
} from '../../../model';
import { ForwardSelectedLabel } from './render-selected-label';
import React, { useRef, useState, useEffect, Key, useMemo } from 'react';
import { Tree } from 'antd';
import { DataNode } from 'antd/lib/tree';
import _ from 'lodash';

// 树型下拉框-支持全选和多选
export const DynamicTreeSelect = <T,>(props: IPropsSelect<T>) => {
  const { isFilterModeView, fieldConfig, selectedValue } = props;
  const filterOptExtension = isSelectComponent(
    fieldConfig.filterOptExtension
  ) as IFieldTypeOptBySelect;
  const isMultiple = fieldConfig.filter_type === FieldType.tree_select;
  const { treeClassName = '', selectAll, treeData } = filterOptExtension;
  const selectAllLabel = filterOptExtension.selectAllLabel || '全选';
  const newValue: string[] = getDynamicSelectedValue(selectedValue, isMultiple);

  const selectedLabelRef = useRef<any | null>(null);
  const [options, setOptions] = useState<Array<IFieldValueExtensionSelect>>([]);
  const [openSelect, setOpenSelect] = useState<boolean>(false);
  const [selectAllValue, setSelectAllValue] = useState<
    Array<SelectOptionValue>
  >([]);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [checkedKeys, setCheckedKeys] = useState<
    React.Key[] | { checked: Key[]; halfChecked: Key[] }
  >([]);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [autoExpandParent, setAutoExpandParent] = useState<boolean>(true);
  const [treeList, setTreeList] = useState<DataNode[] | undefined>([]);
  const [keywords, setKeywords] = useState<string>('');

  useEffect(() => {
    if (
      isFilterModeView &&
      filterOptExtension &&
      fieldConfig.filter_type === FieldType.tree_select
    ) {
      const isSelectAll = isMultiple && selectAll;
      const options: any = getDynamicSelectOption(
        selectedValue.value,
        fieldConfig?.fieldOptExtension?.data || props.options,
        isSelectAll,
        selectAllLabel
      );
      setOptions(options);
      const cloneTree = _.cloneDeep(treeData);
      if (!openSelect) {
        // 判断是否有全选
        const isAllIndex = cloneTree?.findIndex(
          (v) => v.key === AUTO_TABLE_CUSTOM_VALUE_ALL
        );
        if (isSelectAll && cloneTree && isAllIndex === -1) {
          cloneTree.unshift({
            title: selectAllLabel,
            key: AUTO_TABLE_CUSTOM_VALUE_ALL,
          });
        }
        setTreeList(cloneTree);
      }

      // 获取全选列表
      if (isSelectAll && options.length > 0) {
        setSelectAllValue(options.map((item: any) => item.value));
      }
      setCheckedKeys(newValue);
      setSelectedKeys(newValue);
      setExpandedKeys(newValue);
      setAutoExpandParent(true);
    }
  }, [
    isFilterModeView,
    filterOptExtension,
    fieldConfig?.fieldOptExtension?.data,
    props.options,
    openSelect,
  ]);

  const filterTreeData = useMemo(() => {
    // 筛选
    const filter = (data: DataNode[] | undefined) => {
      if (!keywords) {
        return data;
      }
      const filteredData: DataNode[] = [];
      const traverse = (node: DataNode) => {
        const newNode: any = JSON.parse(JSON.stringify(node));
        if (newNode.title.toLowerCase().includes(keywords.toLowerCase())) {
          newNode.checked = true;
          filteredData.push(newNode);
          return;
        }
        newNode.checked = false;

        if (newNode.children) {
          newNode.children.forEach(traverse);
        }
      };
      (data || []).forEach(traverse);

      if (filteredData.length > 0) {
        setExpandedKeys(filteredData.map((v) => v.key));
        setAutoExpandParent(true);
      }
      return filteredData;
    };

    return filter(treeList);
  }, [treeList, keywords]);

  // 排序
  const onDropdownVisibleChange = (open: boolean) => {
    if (open && filterTreeData) {
      addPos(filterTreeData);
      const indexArr: number[] = (checkedKeys as any).map((key: React.Key) => {
        return getParentIndex(filterTreeData, key);
      });
      if (selectAll) {
        indexArr.unshift(0);
      }
      const sortedIndex = Array.from(new Set(indexArr.map((v) => v))).sort(
        (a, b) => a - b
      );
      const sortedData = [];
      for (const index of sortedIndex) {
        if (index > -1) {
          sortedData.push(filterTreeData[index]);
        }
      }
      for (let i = 0; i < filterTreeData.length; i++) {
        if (!sortedIndex.includes(i)) {
          sortedData.push(filterTreeData[i]);
        }
      }
      setTreeList(sortedData);
    }
  };

  // Filters
  if (isFilterModeView) {
    const filterOptExtension = isSelectComponent(
      fieldConfig.filterOptExtension
    );
    if (!filterOptExtension) {
      return null;
    }

    const type = getSelectTypeConfig(filterOptExtension);
    const isTree = filterOptExtension?.kind === FieldType.tree_select;
    const isSelectAll = isMultiple && filterOptExtension.selectAll;
    const placeholder = getPlaceholderConfig(fieldConfig);

    const onExpand = (expandedKeysValue: React.Key[]) => {
      setExpandedKeys(expandedKeysValue);
      setAutoExpandParent(false);
    };

    const onCheck = (
      checkedKeysValue: React.Key[] | { checked: Key[]; halfChecked: Key[] },
      info: any
    ) => {
      let checkedKeys: React.Key[] = (
        checkedKeysValue as {
          checked: Key[];
          halfChecked: Key[];
        }
      ).checked;
      const { checked, node } = info;
      if (checked) {
        // 选中
        checkedKeys = getCheckValue(checkedKeys, node, selectAllValue);
      } else {
        // 取消选中
        checkedKeys = getTraverseValue(checkedKeys, node);
      }

      setCheckedKeys(checkedKeys);
      onChanged(checkedKeys as any);
    };

    const onSelect = (selectedKeysValue: React.Key[], info: any) => {
      let allSelectedKeys = [...selectedKeys];
      const { node } = info;
      if (!allSelectedKeys.includes(node.key)) {
        allSelectedKeys.push(node.key);
        allSelectedKeys = getCheckValue(allSelectedKeys, node, selectAllValue);
      } else {
        allSelectedKeys = allSelectedKeys.filter((v) => v !== node.key);
        allSelectedKeys = getTraverseValue(allSelectedKeys, node);
      }
      setSelectedKeys(allSelectedKeys);
      setCheckedKeys(allSelectedKeys);
      onChanged(allSelectedKeys as any);
    };

    const getCurrentValue = (value: SelectValue) => {
      if (Array.isArray(value) && Array.isArray(newValue)) {
        let currentSelect: any = value.slice();
        if (!newValue.length) return currentSelect;
        const newOptValues = options.map((v) => v.value);
        for (const it of newValue) {
          if (!value.includes(it as unknown as never)) {
            if (!newOptValues.includes(it)) {
              currentSelect = [...currentSelect, it];
            }
          }
        }
        return currentSelect;
      }
      return [];
    };

    const onChanged = (value: SelectValue) => {
      // 确定现在选择的所有值
      const currentSelect = getCurrentValue(value);

      // 当使用 全选功能 时 在选中项的回调中清空子组件的input参数
      if (selectedLabelRef?.current && isSelectAll) {
        selectedLabelRef.current.emptyInput();
      }

      // 当启用全选功能时
      if (isSelectAll && Array.isArray(currentSelect)) {
        const checkItem = getCheckItem(currentSelect, newValue, selectAllValue);
        return props.onChanged(fieldConfig.key, checkItem);
      }

      props.onChanged(fieldConfig.key, currentSelect);
    };

    const onClickClear = () => {
      onChanged([]);
    };

    const onFocus = () => {
      // 当使用 全选功能 才 使用 手动聚焦input框
      if (selectedLabelRef?.current && isSelectAll) {
        selectedLabelRef.current.focusInput();
      }
    };

    const selectValue = (keyWord: string) => {
      setKeywords(keyWord);
    };

    const renderLabel = () => {
      return (
        <ForwardSelectedLabel
          type={type}
          placeholder={placeholder}
          isMultiple={isMultiple}
          selectAllLabel={selectAllLabel}
          fieldConfig={fieldConfig}
          onClickClear={onClickClear}
          newValue={newValue}
          isSelectAll={isSelectAll}
          openSelect={openSelect}
          setOpenSelect={setOpenSelect}
          selectValue={selectValue}
          ref={selectedLabelRef}
        />
      );
    };

    const renderTreeNodes = (data: Array<DataNode> | undefined) => {
      if (data) {
        return (
          <Tree
            checkable
            checkStrictly={true}
            onExpand={onExpand}
            expandedKeys={expandedKeys}
            autoExpandParent={autoExpandParent}
            onCheck={onCheck}
            checkedKeys={checkedKeys}
            onSelect={onSelect}
            selectedKeys={selectedKeys}
            treeData={data}
            className={treeClassName}
          />
        );
      }
    };

    return (
      <Select
        type={type}
        placeholder={placeholder}
        value={newValue}
        onChanged={onChanged}
        options={options}
        mode={isMultiple ? 'multiple' : undefined}
        allowClear={getAllowClearConfig(filterOptExtension)}
        showSearch={true}
        className={props.className}
        renderSelectedLabel={isSelectAll ? renderLabel : undefined}
        onFocus={onFocus}
        open={isSelectAll ? openSelect : undefined}
        {...filterOptExtension}
        dropDownProps={{
          container: () =>
            document.getElementsByClassName('fullscreen-enabled')[0] ||
            document.body,
        }}
        onDropdownVisibleChange={onDropdownVisibleChange}
      >
        {isTree && renderTreeNodes(filterTreeData)}
      </Select>
    );
  }

  const bodyValue = getTableBodyLinkageValue(selectedValue, fieldConfig);
  if (bodyValue) {
    return <span>{bodyValue}</span>;
  }
  return <span>{selectedValue.value}</span>;
};
